import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IPaging } from '../../../interfaces/paging.interface';
import { IPresets, IPtz } from '../../../interfaces/presets.interface';
import { IPitch } from '../../../interfaces/pitch.interface';
import { IStream } from '../../../interfaces/stream.interface';
import {
  Entity,
  INotificationMessage,
} from '../../../sockets/notification.messages';
import { DomHub } from '../../../sockets/dom.hub';
import { IProducer } from '../../../interfaces/producer.interface';
import { IProducerListRequest } from '../interfaces/producer-list-request.interface';
import { ParamsUtil } from '../../../helpers/params-util';
import { filter, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ProducerService {
  domEvents = this.socket.socket.fromEvent<INotificationMessage>('dom');
  private apiUrl = environment.apiUrl + '/Producers';

  constructor(
    private http: HttpClient,
    private socket: DomHub,
  ) {}

  public list(
    params?: Partial<IProducerListRequest>,
  ): Observable<IPaging<IProducer>> {
    return this.http
      .get<
        IPaging<IProducer>
      >(this.apiUrl, { params: ParamsUtil.prepareParams(params) })
      .pipe(
        map((response) => {
          return {
            ...response,
            items: response.items.sort((a, b) => a.name.localeCompare(b.name)),
          };
        }),
      );
  }

  public getProducer(id: string): Observable<IProducer> {
    return this.http.get<IProducer>(`${this.apiUrl}/${id}`);
  }

  public getPresets(): Observable<IPresets[]> {
    return this.http.get<IPresets[]>(`${this.apiUrl}/Presets`);
  }

  public savePitch(pitch: IPitch, recorderId: string): Observable<IProducer> {
    return this.http.put<IProducer>(
      `${this.apiUrl}/${recorderId}/Settings/Pitch`,
      { id: pitch.id },
    );
  }

  public setCameraType(
    recorderSid: string,
    cutout: string,
    cameraType: string,
  ): Observable<IStream> {
    return this.http.put<IStream>(
      `${this.apiUrl}/${recorderSid}/Settings/CameraType`,
      {
        cutout,
        cameraType,
      },
    );
  }

  public savePreset(preset: IPresets): Observable<IPresets> {
    return this.http.put<IPresets>(`${this.apiUrl}/Presets`, preset);
  }

  public updatePreset(preset: IPresets): Observable<IPresets> {
    return this.http.put<IPresets>(
      `${this.apiUrl}/Presets/${preset.sid}`,
      preset,
    );
  }

  public deletePreset(sid: string): Observable<IPresets> {
    return this.http.delete<IPresets>(`${this.apiUrl}/Presets/${sid}`);
  }

  public getPtz(recorderSid: string, cutout: string): Observable<IPtz> {
    return this.http.get<IPtz>(`${this.apiUrl}/${recorderSid}/${cutout}/PTZ`);
  }

  public setPtz(
    recorderSid: string,
    cutout: string,
    phi: number,
    theta: number,
    fov: number,
  ): Observable<IPtz> {
    return this.http.put<IPtz>(`${this.apiUrl}/${recorderSid}/${cutout}/PTZ`, {
      phi,
      theta,
      fov,
    });
  }

  public listenToProducerChanges(): Observable<INotificationMessage> {
    return this.domEvents.pipe(
      filter((data: INotificationMessage) => data.entity === Entity.Producer),
    );
  }
}
